/**
 * What a 401 from our API actually means, and why nothing here navigates.
 *
 * The client sends requests for three different callers, and a blanket
 * redirect-to-login would be wrong for two of them:
 *
 *   - Anonymous visitors. The public event-checkout landing and the marketing
 *     content endpoints use this same instance with no token at all. Bouncing
 *     a visitor to Auth0 because a public page happened to touch a protected
 *     endpoint takes them off the page they came to buy from.
 *   - A signed-in user whose token was refused. Auth0's SDK is already the
 *     thing that renews and, in AuthenticationContext's wrapped getter,
 *     already calls loginWithRedirect for `login_required`,
 *     `consent_required` and `missing_refresh_token`. A second redirect from
 *     down here races that one, and if the page we land on issues the same
 *     failing request the two take turns forever.
 *
 * So the interceptor CLASSIFIES and never navigates: it replaces an opaque
 * 401 body with a sentence that says what went wrong, and lets the existing
 * error paths — the global mutation toast, a page's own Alert — show it.
 */

export const SESSION_EXPIRED_MESSAGE = 'Your session has expired. Sign in again to continue.';

export const SIGN_IN_UNAVAILABLE_MESSAGE =
  "We couldn't confirm you're signed in, so that request was refused. Reload the page and try again.";

export interface RequestAuthState {
  /** A Bearer token was attached to this request. */
  tokenAttached: boolean;
  /** We were signed in and tried to mint a token for this request, and could not. */
  tokenFetchFailed: boolean;
}

export const ANONYMOUS_REQUEST: RequestAuthState = { tokenAttached: false, tokenFetchFailed: false };

/**
 * The message a 401 deserves, or null to leave the response exactly as the
 * server sent it.
 *
 * Null for an anonymous request is the important case: the visitor was never
 * signed in, so "your session expired" is a lie and "sign in again" is advice
 * about a session they never had. Whatever the endpoint said is closer.
 */
export function unauthorizedMessage(
  status: number | undefined,
  auth: RequestAuthState,
): string | null {
  if (status !== 401) return null;
  // The token fetch threw, so we sent no credentials. The 401 is our fault,
  // not a rejected identity, and "expired" would send them to re-authenticate
  // against an Auth0 session that is probably fine.
  if (auth.tokenFetchFailed) return SIGN_IN_UNAVAILABLE_MESSAGE;
  if (auth.tokenAttached) return SESSION_EXPIRED_MESSAGE;
  return null;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Writes `message` into the `{ error }` envelope every consumer already reads
 * through getApiErrorMessage, so an inline <Alert> gets the same wording as
 * the toast without any call site knowing this module exists.
 *
 * Only ever FILLS a gap: an envelope that already carries an `error` string,
 * or a body that is something else entirely, is returned untouched. A 401 the
 * API explained itself is better than anything written here.
 */
export function withApiErrorMessage(data: unknown, message: string): unknown {
  if (data === null || data === undefined || data === '') return { error: message };
  if (!isPlainObject(data)) return data;
  if (typeof data.error === 'string' && data.error.trim()) return data;
  return { ...data, error: message };
}
